/**
 * As respostas do atendente que o parceiro ainda não leu (D3, passo 19 — peça I do desenho do portal): a chave do cache e as três
 * operações que o socket e a tela fazem nele. Fonte única: `GET /portal/atendimentos/nao-lidas` (`total` + `por_sessao`).
 * O `data` é substituído por um objeto NOVO só quando algo muda (a referência estável evita renderizar à toa).
 */
import type { QueryClient } from "@tanstack/react-query";
import type { NaoLidasDoAtendimento } from "@/types/atendimento";

export const CHAVE_DAS_NAO_LIDAS_DO_ATENDIMENTO = ["portal-atendimentos", "nao-lidas"] as const;

export function totalDeNaoLidas(dados?: NaoLidasDoAtendimento | null): number {
  return Math.max(0, dados?.total ?? 0);
}

/** "1 resposta não lida" / "N respostas não lidas" — o texto do leitor de tela (o menu acrescenta " no atendimento"). */
export function textoDasNaoLidas(total: number): string {
  return total === 1 ? "1 resposta não lida" : `${total} respostas não lidas`;
}

/**
 * Chegou uma resposta com a conversa FORA de vista: +1 no total e na sessão. Sem cache ainda não inventa um — a busca em voo já traz o
 * número do servidor (o evento sai depois do commit).
 * ⚠️ O `setQueryData` NÃO cancela um GET já em voo (que pode ter lido antes do commit e apagaria o +1): quem chama invalida a chave logo
 * depois (`aoVivoDoPortal.ts`), o que cancela a busca velha e refaz.
 */
export function somarRespostaNova(qc: QueryClient, sessionId: string): void {
  qc.setQueryData<NaoLidasDoAtendimento>(CHAVE_DAS_NAO_LIDAS_DO_ATENDIMENTO, (atual) =>
    atual
      ? {
          total: atual.total + 1,
          por_sessao: { ...atual.por_sessao, [sessionId]: (atual.por_sessao[sessionId] ?? 0) + 1 },
        }
      : atual
  );
}

/** `atendimento:lidas`: o total passa a ser o do servidor (depois da leitura) e a sessão zera. */
export function aplicarLidas(qc: QueryClient, sessionId: string, total: number): void {
  qc.setQueryData<NaoLidasDoAtendimento>(CHAVE_DAS_NAO_LIDAS_DO_ATENDIMENTO, (atual) =>
    atual
      ? { total: Math.max(0, total), por_sessao: { ...atual.por_sessao, [sessionId]: 0 } }
      : atual
  );
}

/** Abrir a conversa (otimista, sem esperar o servidor): desconta a sessão do total e a zera. */
export function zerarSessaoNoCache(qc: QueryClient, sessionId: string): void {
  qc.setQueryData<NaoLidasDoAtendimento>(CHAVE_DAS_NAO_LIDAS_DO_ATENDIMENTO, (atual) => {
    if (!atual) return atual;
    const daSessao = atual.por_sessao[sessionId] ?? 0;
    if (daSessao === 0) return atual;
    return {
      total: Math.max(0, atual.total - daSessao),
      por_sessao: { ...atual.por_sessao, [sessionId]: 0 },
    };
  });
}
